import { useEffect, useRef } from 'react';
import { differenceInHours } from 'date-fns';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSiteUserTraits } from '@confluence/targeting/entry-points/useSiteUserTraits.ts';
import { useSiteTraits } from '@confluence/targeting/entry-points/useSiteTraits.ts';
import {
	CREATION_FOCUSED_NAV_START_TIME,
	SIMPLIFY_NAV_NEW_USER,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { type useGetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';

const CONFLUENCE_FIRST_ACTIVE_TRAIT = 'confluence_first_active';
const FIRST_ACTIVATION_DATE_TRAIT = 'firstActivationDate';

type OnboardingStateResult = ReturnType<typeof useGetOnboardingState>;

type useIsEligibleForSimplifyNavXflowProps = {
	onboardingStateData: OnboardingStateResult['data'];
	onboardingStateLoading: OnboardingStateResult['loading'];
	onboardingStateError: OnboardingStateResult['error'];
	skip: boolean;
};

type useIsEligibleForSimplifyNavXflowResult = {
	isEligible: boolean;
	isLoading: boolean;
	hasError: boolean;
	isWithinD0Window: boolean;
};

export const useIsEligibleForSimplifyNavXflow = ({
	onboardingStateData,
	onboardingStateLoading,
	onboardingStateError,
	skip,
}: useIsEligibleForSimplifyNavXflowProps): useIsEligibleForSimplifyNavXflowResult => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const {
		data: siteUserTraitsData,
		loading: siteUserTraitsLoading,
		error: siteUserTraitsError,
	} = useSiteUserTraits({ skipBootstrap: skip });

	const {
		data: siteTraitsData,
		loading: siteTraitsDataLoading,
		error: siteTraitsDataError,
	} = useSiteTraits({ skipBootstrap: skip });

	const entries = onboardingStateData?.onboardingState;

	const isEnrolledInPreviousExperiment = !!entries?.find(
		(s) => s.key === CREATION_FOCUSED_NAV_START_TIME,
	)?.value;

	const isEligibleForSimplifyNavNewUser =
		entries?.find(({ key }) => key === SIMPLIFY_NAV_NEW_USER.NTH_USER_D0_SIMPLIFIED_NAV_ELIGIBLE)
			?.value === 'true';

	const firstActiveTrait = siteUserTraitsData?.attributes?.find(
		({ name }) => name === CONFLUENCE_FIRST_ACTIVE_TRAIT,
	)?.value;

	const siteActivationDateTrait = siteTraitsData?.attributes?.find(
		({ name }) => name === FIRST_ACTIVATION_DATE_TRAIT,
	)?.value;

	let hoursSinceFirstActiveTrait, hoursSinceSiteActivationDate;
	try {
		hoursSinceFirstActiveTrait = firstActiveTrait
			? differenceInHours(Date.now(), new Date(firstActiveTrait))
			: -1;
		hoursSinceSiteActivationDate = siteActivationDateTrait
			? differenceInHours(Date.now(), new Date(siteActivationDateTrait))
			: -1;
	} catch {
		hoursSinceFirstActiveTrait = -2;
		hoursSinceSiteActivationDate = -2;
	}

	const isLoading = !!(onboardingStateLoading || siteUserTraitsLoading || siteTraitsDataLoading);
	const hasError = !!(onboardingStateError || siteUserTraitsError || siteTraitsDataError);

	const hasFiredAnalytic = useRef(false);

	useEffect(() => {
		if (
			!isLoading &&
			!hasError &&
			!isEnrolledInPreviousExperiment &&
			!isEligibleForSimplifyNavNewUser &&
			!hasFiredAnalytic.current &&
			!skip
		) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'eligible',
					actionSubject: 'cfind-5565-simplify-nav-xflow',
					actionSubjectId: 'cfind-5565-simplify-nav-xflow',
					source: 'cfind-5565-simplify-nav-xflow',
					attributes: {
						firstActive: firstActiveTrait,
						hoursSinceFirstActiveTrait,
						siteActivationDate: siteActivationDateTrait,
						hoursSinceSiteActivationDate,
					},
				},
			}).fire();
			hasFiredAnalytic.current = true;
		}
	}, [
		createAnalyticsEvent,
		isLoading,
		isEnrolledInPreviousExperiment,
		isEligibleForSimplifyNavNewUser,
		firstActiveTrait,
		hasError,
		hoursSinceFirstActiveTrait,
		hoursSinceSiteActivationDate,
		siteActivationDateTrait,
		skip,
	]);
	return {
		isLoading: false,
		hasError: false,
		isEligible: false,
		isWithinD0Window: false,
	};
};
